import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import { faCircleExclamation, faCirclePlus } from "@fortawesome/pro-regular-svg-icons";

import { Modal } from "@/components";
import { MappingFieldRow } from "@/components/ColumnMapping/MappingFieldRow";
import { AutoCompleteItem } from "@/components/Dropdown/Dropdown";
import { Icon } from "@/components/Icon/Icon";
import { Spinner } from "@/components/Spinner/Spinner";

import { TrackerEntityKind } from "@/enum/tracker.enum";

import { classNames } from "@/utils/classNames";

import {
	CUSTOM_FIELD,
	entityKindKey,
	isIdentifierField,
	mappingOptions,
	mappingSatisfiesEntityKind,
} from "./trackerSourceRequirements";

export interface TrackerColumnMapping {
	foundField: string;
	assignedField: string;
	/** First non-empty value of the column, shown so the user can recognise it. */
	sample?: string;
}

interface TrackerMappingModalProps {
	fileName: string;
	entityKind: TrackerEntityKind;
	mapping: TrackerColumnMapping[];
	isSuggesting: boolean;
	isSaving: boolean;
	onChange: (mapping: TrackerColumnMapping[]) => void;
	onClose: () => void;
	onConfirm: () => void;
}

const SKIP_OPTION = "";

/**
 * Mapping a file's columns onto tracker fields — and refusing to continue until the
 * identifier is among them (PREC-3417).
 *
 * The refusal is the point of the ticket: a file whose identifier is never mapped imports
 * zero profiles, and users could not tell that had happened. Here the required row is
 * highlighted, the footer says what is missing, and the confirm button stays disabled — so
 * the constraint is met before anything is uploaded rather than discovered afterwards.
 */
export const TrackerMappingModal: FunctionComponent<TrackerMappingModalProps> = ({
	fileName,
	entityKind,
	mapping,
	isSuggesting,
	isSaving,
	onChange,
	onClose,
	onConfirm,
}) => {
	const { t } = useTranslation();

	const options = useMemo(() => mappingOptions(entityKind), [entityKind]);
	const isSatisfied = useMemo(
		() => mappingSatisfiesEntityKind(mapping, entityKind),
		[mapping, entityKind]
	);

	/** A target already used by another column is hidden, so two columns cannot claim it. */
	const availableOptions = (current: string): AutoCompleteItem[] => {
		const taken = new Set(
			mapping.map((row) => row.assignedField).filter((field) => field && field !== current)
		);

		return [
			{ title: t("profileTracker.sources.mapping.skip"), id: SKIP_OPTION },
			...options
				.filter((option) => !taken.has(option.id))
				.map((option) => ({ title: option.title, id: option.id })),
			{ title: t("profileTracker.sources.mapping.custom"), id: CUSTOM_FIELD },
		];
	};

	const selectFor = (row: TrackerColumnMapping): AutoCompleteItem => {
		if (row.assignedField === CUSTOM_FIELD) {
			return { title: t("profileTracker.sources.mapping.custom"), id: CUSTOM_FIELD };
		}

		const option = options.find((candidate) => candidate.id === row.assignedField);

		return option
			? { title: option.title, id: option.id }
			: { title: t("profileTracker.sources.mapping.skip"), id: SKIP_OPTION };
	};

	const assign = (index: number, assignedField: string) => {
		onChange(
			mapping.map((row, rowIndex) => (rowIndex === index ? { ...row, assignedField } : row))
		);
	};

	const subtitleKey =
		entityKindKey(entityKind) === "companies" ? "subtitleCompanies" : "subtitlePeople";

	return (
		<Modal
			isOpened
			// The whole point: the source cannot be added until its identifier is mapped.
			disableSave={!isSatisfied}
			handleClose={onClose}
			handleSave={onConfirm}
			isLoading={isSaving}
			// The default "xs" dialog is 444px — narrower than a column name plus its
			// target dropdown and preview, so the mapping used to scroll sideways with
			// the dropdowns cut off at the edge.
			size="md"
			submitButtonText={t("profileTracker.sources.mapping.submit")}
			title={t("profileTracker.sources.mapping.title")}
		>
			<div className="flex flex-col gap-4">
				<p className="text-ssm text-text-meta">
					{t(`profileTracker.sources.mapping.${subtitleKey}`, {
						count: mapping.length,
						file: fileName,
					})}
				</p>

				{isSuggesting && (
					<div className="flex items-center gap-1.5 text-xs text-text-meta">
						<Spinner color="#4d66f5" />
						<span>{t("profileTracker.sources.mapping.analyzing")}</span>
					</div>
				)}

				<div className="flex items-center justify-between gap-2 text-xs font-semibold uppercase tracking-wide text-text-meta">
					<span>{t("profileTracker.sources.mapping.columnHeader")}</span>
					<div className="flex flex-row gap-2">
						<span className="w-[250px]">{t("profileTracker.sources.mapping.targetHeader")}</span>
						<span className="w-[160px]">{t("profileTracker.sources.mapping.previewHeader")}</span>
					</div>
				</div>

				<ul>
					{mapping.map((row, index) => {
						// Only the row that carries the identifier is marked. Tinting every row
						// while the requirement is unmet (the previous rule) painted the whole
						// table warning-yellow and pointed at nothing.
						const isRequiredRow = isIdentifierField(entityKind, row.assignedField);

						return (
							<MappingFieldRow
								key={row.foundField}
								badge={
									isRequiredRow ? (
										<span className="inline-flex items-center gap-1 rounded-md border border-status-warning bg-white px-1.5 py-0.5 text-[10.5px] font-bold text-status-warning">
											<Icon className="h-3 w-3" icon={faCircleExclamation} />
											{t("profileTracker.sources.mapping.requiredBadge")}
										</span>
									) : row.assignedField === CUSTOM_FIELD ? (
										<span className="inline-flex items-center gap-1 text-xs font-semibold text-brand-light">
											<Icon className="h-3 w-3" icon={faCirclePlus} />
											{t("profileTracker.sources.mapping.customFlag")}
										</span>
									) : undefined
								}
								foundField={row.foundField}
								handleSelect={(value?: AutoCompleteItem) =>
									assign(index, value?.id?.toString() ?? SKIP_OPTION)
								}
								highlight={isRequiredRow}
								isMapped={!!row.assignedField}
								options={availableOptions(row.assignedField)}
								previewValue={row.sample}
								selectedField={selectFor(row)}
								// Files exported from a CRM often have headers like "Field 4" — the
								// value is what makes the right choice obvious, so it shows before
								// the column is mapped too.
								showPreviewWhenUnmapped
								// The menu has to escape the dialog, which clips an inline one.
								portalMenu
							/>
						);
					})}
				</ul>

				<div
					className={classNames(
						"text-ssm font-semibold",
						isSatisfied ? "text-status-success" : "text-status-warning"
					)}
					data-testid="tracker-mapping-status"
				>
					{isSatisfied
						? t("profileTracker.sources.mapping.ready")
						: t("profileTracker.sources.mapping.missing")}
				</div>
			</div>
		</Modal>
	);
};
